// Incidents list — Session B rebuild on the FwTable pattern.
//
// The API request is IDENTICAL in shape to what AgTable sent from this page:
//   GET admin/incident?sortBy=..&sortDir=..&offset=<page+1>&limit=..&query={...}
// with auth: "token". Only the rendering layer changed.
import { Fragment, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";

import PageBreadcrumb from "elements/System/PageBreadcrumb";
import FwTable, { FwColumn, FwQuery, FwFetchResult } from "elements/Table/FwTable";
import SidePanel, { PanelField } from "elements/Panel/SidePanel";
import FieldSelect from "elements/System/FieldSelect";
import { api, common } from "includes";
import moment from "moment";

const COLUMNS: FwColumn[] = [
  { label: "Title", key: "field1Value", sort: true },
  { label: "Locality", key: "locality", type: "locality" },
  { label: "Address", key: "address" },
  { label: "Created By", key: "createdBy", type: "obj" },
  { label: "Version", key: "versionNumber", type: "num" },
  { label: "Created", key: "createdAt", sort: true, type: "date_time" },
  // Sortable so "what changed recently" is one header click, which is the
  // question an audit column actually gets asked.
  { label: "Updated", key: "updatedAt", sort: true, type: "date_time", minWidth: 1200 },
  {
    label: "",
    key: "featuredImageUrl",
    // What the apps actually show as an incident's hero image, so it reads at
    // a glance rather than hiding behind a hover action.
    render: (row: any) =>
      row.featuredImageUrl ? (
        <i
          className="ri-star-fill"
          style={{ color: "var(--fw-warning, #e0a800)" }}
          title="Has a featured image"
        />
      ) : null,
  },
];

/** createdBy/modifiedBy arrive populated to {firstName,lastName}. */
const adminName = (who: any) =>
  who ? [who.firstName, who.lastName].filter(Boolean).join(" ") : "";

/** Mongoose stamps updatedAt on insert too, so an untouched record reports an
 *  "update" identical to its creation. Treat a second-level match as never
 *  edited rather than showing the admin a meaningless revision. */
const wasEdited = (row: any) =>
  Boolean(row?.updatedAt) &&
  Math.abs(
    new Date(row.updatedAt).getTime() - new Date(row.createdAt).getTime()
  ) > 1000;

export default function Incidents() {
  const navigate = useNavigate();
  const [permission] = useState(common.getUserPermission());
  const [openRow, setOpenRow] = useState<any>(null);
  // Row actions that reach people (a push) or change what the apps show
  // (clearing a featured image) confirm first and announce their result.
  const [confirmAct, setConfirmAct] = useState<{ type: string; row: any } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const runAction = () => {
    if (!confirmAct) return;
    const { type, row } = confirmAct;
    setBusyId(row._id);
    setConfirmAct(null);

    const param =
      type === "push"
        ? {
            // Dedicated endpoint, not a PUT carrying sendPushNotification:
            // Update would bump versionNumber and make a re-send look like a
            // revision in the audit trail.
            url: `admin/incident/${row._id}/resend-push`,
            method: "POST",
            auth: "token",
            body: {},
          }
        : {
            url: `admin/incident/${row._id}`,
            method: "PUT",
            auth: "token",
            body: { featured: false, featuredImageUrl: null },
          };

    api.call(param, (res: any) => {
      setBusyId(null);
      if (res.status === 200) {
        common.notify(
          "S",
          type === "push"
            ? "Push notification queued"
            : "Featured image removed"
        );
        // Only the unfeature changes a row; a queued push leaves the list as is.
        if (type !== "push") setReloadKey((k) => k + 1);
      } else {
        common.notify("E", res?.data?.error || "Action failed");
      }
    });
  };

  if (!permission?.includes("inr")) {
    common.redirect("app/home");
  }

  // Same request AgTable made from this page, verbatim in shape.
  const fetcher = useCallback(
    (q: FwQuery, cb: (r: FwFetchResult) => void) => {
      let query = `?sortBy=${q.sortBy}&sortDir=${q.sortDir}`;
      query += `&offset=${q.page + 1}&limit=${q.limit}`;
      query += `&query=${JSON.stringify(q.filter)}`;

      api.call(
        { url: "admin/incident", method: "GET", auth: "token", query },
        (res: any) => {
          if (res.status === 200) {
            cb({
              rows: res.data?.data || [],
              total: res.data?.pageInfo?.totalCount || 0,
            });
          } else {
            cb({ rows: [], total: 0 });
          }
        }
      );
    },
    []
  );

  return (
    permission.includes("inr") && (
      <Fragment>
        <PageBreadcrumb
          breadCrumbItems={[{ label: "Incidents", path: "/incident/list", active: true }]}
          title={"Incidents"}
        />

        <FwTable
          module="incident"
          columns={COLUMNS}
          fetcher={fetcher}
          defaultSort={{ sortBy: "createdAt", sortDir: "desc" }}
          quickFilterKey="field1Value"
          quickFilterPlaceholder="Filter by title…"
          extraFilters={(filter, set) => (
            <div className="fw-select" style={{ minWidth: 190 }}>
              <FieldSelect
                api="admin/locality"
                query="?offset=1&limit=1000"
                get="name"
                set="_id"
                value={null}
                placeholder="All localities"
                render={"yes"}
                clearable={true}
                multi={false}
                onSelect={(data: any) =>
                  set({ locality: data?.value ? [data.value] : [] })
                }
              />
            </div>
          )}
          toolbar={
            permission.includes("inc") ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigate("/incident/form?mode=add")}
              >
                <i className="ri-add-line" /> New incident
              </button>
            ) : undefined
          }
          actions={(row) => (
            <Fragment>
              <button
                type="button"
                className="fw-rowact"
                title="View"
                onClick={() => setOpenRow(row)}
              >
                <i className="ri-eye-line" />
              </button>
              {permission.includes("inu") && (
                <Fragment>
                  {/* Only offered where there is an image to clear. Setting one
                      means choosing which comment photo, which belongs to the
                      comments panel on the full record, not to a row action. */}
                  {row.featuredImageUrl && (
                    <button
                      type="button"
                      className="fw-rowact"
                      title="Remove featured image"
                      disabled={busyId === row._id}
                      onClick={() => setConfirmAct({ type: "unfeature", row })}
                    >
                      <i className="ri-star-off-line" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="fw-rowact"
                    title="Re-send push notification"
                    disabled={busyId === row._id}
                    onClick={() => setConfirmAct({ type: "push", row })}
                  >
                    <i className="ri-notification-3-line" />
                  </button>
                  <button
                    type="button"
                    className="fw-rowact"
                    title="Edit"
                    onClick={() =>
                      navigate(`/incident/form?id=${row._id}&mode=edit`)
                    }
                  >
                    <i className="ri-edit-line" />
                  </button>
                </Fragment>
              )}
            </Fragment>
          )}
          reloadKey={reloadKey}
          onRowOpen={(row) => setOpenRow(row)}
          emptyText="No incidents match this view."
        />

        {/* Session B groundwork: read-only summary; Session C makes it edit-in-place */}
        <SidePanel
          open={Boolean(openRow)}
          title={openRow?.field1Value || "Incident"}
          onClose={() => setOpenRow(null)}
          footer={
            <Fragment>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  navigate(`/incident/form?id=${openRow?._id}&mode=view`)
                }
              >
                Open full record
              </button>
              {permission.includes("inu") && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    navigate(`/incident/form?id=${openRow?._id}&mode=edit`)
                  }
                >
                  <i className="ri-edit-line" /> Edit
                </button>
              )}
            </Fragment>
          }
        >
          {openRow && (
            <Fragment>
              <PanelField label="Locality">
                {openRow.locality?.name || "—"}
                {openRow.subLocality?.name ? ` · ${openRow.subLocality.name}` : ""}
              </PanelField>
              <PanelField label="Address">{openRow.address || "—"}</PanelField>
              {openRow.field2Value && (
                <PanelField label="Description">{openRow.field2Value}</PanelField>
              )}
              <PanelField label="Responding units">
                {Array.isArray(openRow.respondingUnits) && openRow.respondingUnits.length
                  ? openRow.respondingUnits
                      .map((u: any) => (typeof u === "string" ? u : u?.unitName))
                      .filter(Boolean)
                      .join(", ")
                  : "—"}
              </PanelField>
              <PanelField label="Created">
                {openRow.createdAt
                  ? moment(openRow.createdAt).format("DD MMM YYYY, h:mm A")
                  : "—"}
                {adminName(openRow.createdBy)
                  ? ` · ${adminName(openRow.createdBy)}`
                  : ""}
              </PanelField>
              {/* Only worth a row once the record has actually been revised —
                  on a new incident updatedAt just restates Created. */}
              {wasEdited(openRow) && (
                <PanelField label="Last updated">
                  {moment(openRow.updatedAt).format("DD MMM YYYY, h:mm A")}
                  {adminName(openRow.modifiedBy)
                    ? ` · ${adminName(openRow.modifiedBy)}`
                    : ""}
                  {` · ${moment(openRow.updatedAt).fromNow()}`}
                </PanelField>
              )}
              <PanelField label="Version">{openRow.versionNumber || 0}</PanelField>
              <PanelField label="Delivery">
                {openRow.sendPushNotification ? "Push sent" : "No push"}
                {openRow.featured ? " · Featured" : ""}
              </PanelField>
            </Fragment>
          )}
        </SidePanel>

        {/* AgDelete is hard-wired to delete wording, and neither of these is a
            delete — a re-send in particular needs to say who it reaches. */}
        <Modal show={Boolean(confirmAct)} onHide={() => setConfirmAct(null)}>
          <Modal.Header closeButton>
            <h4 className="modal-title">
              {confirmAct?.type === "push"
                ? "Re-send push notification"
                : "Remove featured image"}
            </h4>
          </Modal.Header>
          <Modal.Body>
            {confirmAct?.type === "push" ? (
              <Fragment>
                <p className="fs-16 mb-1">
                  Send a new push notification for{" "}
                  <strong>{confirmAct?.row?.field1Value || "this incident"}</strong>
                  {confirmAct?.row?.address ? ` at ${confirmAct.row.address}` : ""}?
                </p>
                <p className="text-muted mb-0">
                  Everyone following this locality is alerted again. This cannot
                  be undone.
                </p>
              </Fragment>
            ) : (
              <Fragment>
                <p className="fs-16 mb-1">
                  Remove the featured image from{" "}
                  <strong>{confirmAct?.row?.field1Value || "this incident"}</strong>?
                </p>
                <p className="text-muted mb-0">
                  The incident keeps its comments — only the image the apps show
                  at the top is cleared. You can feature another from the
                  comments on the full record.
                </p>
              </Fragment>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setConfirmAct(null)}>
              <i className="ri-close-line me-1" />
              Cancel
            </Button>
            <Button
              variant={confirmAct?.type === "push" ? "primary" : "danger"}
              onClick={runAction}
            >
              <i className="ri-check-line me-1" />
              {confirmAct?.type === "push" ? "Send it" : "Remove"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Fragment>
    )
  );
}
